import { FC, KeyboardEvent, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import { IUserProfile } from '@stores/profile/types';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { emailPattern } from '@utils/patterns';
import { Input } from '@inputs/Input/Input';
import { Button } from '@components/Core/Button';
import GoogleIcon from '@icons/auth/google_icon.svg?component';
import AppleIcon from '@icons/auth/apple_icon.svg?component';
import { IAuthForm, LoginFormTransFields } from './types';
import { loginFormTranslations } from './translations';

const SOC_BUTTON_STYLE =
  'flex-auto h-[48px] border border-primary bg-secondary rounded-[12px] disabled:opacity-40';

const LoginForm: FC = () => {
  const {
    authStore: { sendCreds, setAuthStep, loginWithSoc },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<LoginFormTransFields>([loginFormTranslations.name]);

  const { mutate, isPending } = useMutation({
    mutationFn: sendCreds,
    mutationKey: ['sendCreds'],
    onSuccess: () => {
      setAuthStep('code');
    },
  });

  const { mutate: loginWithSocMutation, isPending: isPendingLoginWithSoc } = useMutation({
    mutationFn: loginWithSoc,
    mutationKey: ['loginWithSoc'],
    onSuccess: (res: IUserProfile | undefined) => {
      if (res && !res.isAgreedPolicy) setAuthStep('finish');
    },
  });

  const useFormMethods = useForm<IAuthForm>();

  const { handleSubmit, watch } = useFormMethods;

  const onLogin = useCallback((data: IAuthForm) => {
    mutate(data);
  }, []);

  const onInputKeyPress = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission on Enter key
      handleSubmit(onLogin)();
    }
  }, []);

  const googleLogin = useGoogleLogin({
    // In case we need auth-code flow
    // flow: 'auth-code',
    // ux_mode: 'popup',
    onSuccess: (codeResponse) => {
      loginWithSocMutation({ accessToken: codeResponse.access_token, providerName: 'google' });
    },
    onError: (error) => console.error('Login Failed:', error),
  });

  const onGoogleLogin = useCallback(() => {
    googleLogout();
    googleLogin();
  }, []);

  const appleLogin = useCallback(async () => {
    if (import.meta.env.MODE === 'development') {
      showAlert('Unavailable local', 'warning');
      return;
    }
    try {
      const response = await window.AppleID.auth.signIn();
      const token = response.authorization.id_token;
      loginWithSocMutation({ accessToken: token, providerName: 'apple' });
    } catch (error) {
      console.error('Apple sign-in failed', error);
    }
  }, []);

  return (
    // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore
    <FormProvider {...useFormMethods}>
      <form className="flex flex-col gap-y-[24px]">
        <Input
          name="email"
          label={t('Email')}
          type="text"
          registerPattern={emailPattern}
          required
          onKeyDown={onInputKeyPress}
        />
        <Button
          onClick={handleSubmit(onLogin)}
          text={t('Login')}
          disabled={isPending || !watch('email') || isPendingLoginWithSoc}
        />
        <div className="relative">
          <div className="absolute top-1/2 w-full h-[1px] bg-[#CDD8F7] z-0" />
          <p className="relative w-max m-auto px-[16px] extra-small-text text-[#0A2361] text-center bg-white z-10">
            {t('OrSignUpWith')}
          </p>
        </div>
        <div className="relative flex w-full justify-between gap-x-[12px]">
          <button
            type="button"
            onClick={onGoogleLogin}
            className={SOC_BUTTON_STYLE}
            disabled={isPending || isPendingLoginWithSoc}
          >
            <GoogleIcon className="m-auto" />
          </button>
          <button
            type="button"
            onClick={() => appleLogin()}
            className={SOC_BUTTON_STYLE}
            disabled={isPending || isPendingLoginWithSoc}
          >
            <AppleIcon className="m-auto" />
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default LoginForm;
